import React from 'react';
import {
  Grid,
  OutlinedInput,
  InputLabel,
  Stack,
  FormHelperText,
  InputAdornment,
  Autocomplete,
  Typography,
  TextField,
  Box,
  Select,
  MenuItem
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { useTheme } from '@mui/material/styles';
import { COMPANY_URL_SUFFIX, fiscalYearValue } from 'constant/AppConfig';

const autocompleteStyle = {
  '.MuiOutlinedInput-root': {
    padding: '3px'
  },
  '.MuiAutocomplete-popupIndicator': {
    height: '30px'
  },
  '.MuiAutocomplete-clearIndicator': {
    height: '30px'
  }
};

const CompanyInfo = ({ control, errors, setValue, groupCompanies, clearErrors }) => {
  const theme = useTheme();

  return (
    <Grid
      container
      spacing={2}
    >
      <Grid
        item
        xs={12}
        sm={6}
      >
        <Stack spacing={1}>
          <InputLabel htmlFor="company_name">Company Name</InputLabel>
          <Controller
            name="company_name"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <OutlinedInput
                autoComplete={'on'}
                id="company_name"
                type="text"
                value={field.value}
                name={field.name}
                onBlur={() => {
                  field.onBlur();
                  setValue('unique_url_component', field?.value?.split(' ')?.[0]?.toLowerCase());
                }}
                onChange={field.onChange}
                placeholder="Company Name"
                fullWidth
                error={Boolean(errors.company_name)}
              />
            )}
          />
          {errors.company_name?.message && (
            <FormHelperText
              error
              id="standard-weight-helper-text-firstName"
            >
              {errors.company_name?.message}
            </FormHelperText>
          )}
        </Stack>
      </Grid>
      <Grid
        item
        xs={12}
        sm={6}
      >
        <Stack spacing={1}>
          <InputLabel htmlFor="web_url">Company Website</InputLabel>
          <Controller
            name="web_url"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <OutlinedInput
                id="web_url"
                autoComplete={'on'}
                type="text"
                value={field.value}
                name={field.name}
                onBlur={field.onBlur}
                onChange={field.onChange}
                placeholder="Company Website"
                fullWidth
                error={Boolean(errors.web_url)}
              />
            )}
          />
          {errors.web_url?.message && (
            <FormHelperText
              error
              id="standard-weight-helper-text-web_url"
            >
              {errors.web_url?.message}
            </FormHelperText>
          )}
        </Stack>
      </Grid>
      <Grid
        item
        xs={12}
      >
        <Stack spacing={1}>
          <InputLabel htmlFor="unique_url_component">Unique URL</InputLabel>
          <Controller
            name="unique_url_component"
            control={control}
            defaultValue=""
            sx={{ padding: '0px auto' }}
            render={({ field }) => (
              <OutlinedInput
                sx={{ padding: '0px 5px' }}
                autoComplete={'on'}
                startAdornment={
                  <InputAdornment
                    sx={{ background: theme.palette.slate[200], height: '28px', width: '95px', borderRadius: '6px', padding: '2px 10px' }}
                    position="start"
                  >
                    https://
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment
                    sx={{ background: theme.palette.slate[200], height: '28px', borderRadius: '6px', padding: '2px 10px' }}
                    position="end"
                  >
                    {COMPANY_URL_SUFFIX}
                  </InputAdornment>
                }
                id="unique_url_component"
                type="text"
                value={field.value}
                name={field.name}
                onBlur={field.onBlur}
                onChange={(e) => {
                  const value = e.target.value?.toLowerCase();
                  field.onChange(value);
                }}
                placeholder="Your URL e.g. biosero"
                fullWidth
                error={Boolean(errors.unique_url_component)}
              />
            )}
          />
          {errors.unique_url_component?.message && (
            <FormHelperText
              error
              id="standard-weight-helper-text-unique_url_component"
            >
              {errors.unique_url_component?.message}
            </FormHelperText>
          )}
        </Stack>
      </Grid>
      <Grid
        item
        xs={12}
        sm={12}
      >
        <Stack spacing={1}>
          <InputLabel htmlFor="email-login">Company Support Email</InputLabel>
          <Controller
            name="email"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <OutlinedInput
                id="email"
                type="email"
                value={field?.value}
                name={field.name}
                onBlur={field.onBlur}
                onChange={field.onChange}
                placeholder="Enter Email"
                autoComplete={'on'}
                fullWidth
                error={Boolean(errors.email)}
              />
            )}
          />
          {errors.email?.message && (
            <FormHelperText
              error
              id="standard-weight-helper-text-email-login"
            >
              {errors.email?.message}
            </FormHelperText>
          )}
        </Stack>
      </Grid>
      <Grid
        item
        xs={12}
        sm={12}
      >
        <Stack spacing={1}>
          <InputLabel htmlFor="fiscal_start_month">Company Fiscal Year Period</InputLabel>
          <Controller
            //   name="state"
            name={'fiscal_start_month'}
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Select
                displayEmpty
                id="fiscal_start_month"
                value={field?.value ?? ''}
                name={field.name}
                onBlur={field.onBlur}
                onChange={(e) => {
                  field.onChange(e);
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 250 // Set the desired max height for the dropdown
                    }
                  }
                }}
                fullWidth
                error={Boolean(errors?.fiscal_start_month)}
                // renderValue={(selected) => {
                //   if (selected?.length === 0) {
                //     return <em>State / Province</em>;
                //   }
                //   return selected;
                // }}
              >
                <MenuItem
                  disabled
                  value=""
                >
                  Select Fiscal Year Period
                </MenuItem>
                {Object.keys(fiscalYearValue)?.map((key) => (
                  <MenuItem value={key}>{fiscalYearValue[key].period}</MenuItem>
                ))}
              </Select>
            )}
          />
          {errors?.fiscal_start_month?.message && (
            <FormHelperText
              error
              id="standard-weight-helper-text-fiscal_start_month"
            >
              {errors?.fiscal_start_month?.message}
            </FormHelperText>
          )}
        </Stack>
      </Grid>
      <Grid
        item
        xs={12}
        mt={1}
      >
        <Stack spacing={1}>
          <InputLabel htmlFor="group_of_companies">Select Group Companies</InputLabel>
          <Controller
            name="group_of_companies"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                id="group_of_companies"
                // Create 5 dummy options for testing
                options={groupCompanies || []}
                getOptionLabel={(option) => option.company_name}
                disableCloseOnSelect
                name={field.name}
                value={field.value}
                isOptionEqualToValue={(option, value) => (option.id || option.tenant_id) === (value.id || value.tenant_id)}
                onChange={(e, value) => {
                  setValue('group_of_companies', value);
                  if (value?.length >= 2) {
                    clearErrors('group_of_companies');
                  }
                }}
                onBlur={field.onBlur}
                sx={autocompleteStyle}
                renderOption={(props, option, { selected }) => (
                  <Stack
                    direction={'row'}
                    spacing={1}
                    {...props}
                  >
                    <Box>
                      <Typography variant={'body1'}>{option.company_name}</Typography>
                      {/* <Typography
                        variant={'body2'}
                        color="secondary"
                      >
                        {option.support_email}
                      </Typography> */}
                    </Box>
                  </Stack>
                )}
                renderInput={(params) => (
                  <TextField
                    error={Boolean(errors.group_of_companies)}
                    {...params}
                    placeholder="Select group companies"
                  />
                )}
              />
            )}
          />
          {errors.group_of_companies?.message && (
            <FormHelperText
              error
              id="standard-weight-helper-text-group_of_companies"
            >
              {errors.group_of_companies?.message}
            </FormHelperText>
          )}
        </Stack>
      </Grid>
    </Grid>
  );
};

export default CompanyInfo;
